import {useTranslation} from "react-i18next";
import {useRouteMatch} from "react-router-dom";
import {i18n} from "i18next";

import {CitySlug} from "../../config/cities";
import {chartsRoutes} from "../../web/src/common/app/routing/charts_routes";
import {withDateGuard} from "../../web/src/components/hoc/withDateGuard";
import {useSelectedRegionDisplay} from "../../web/src/features/analytical_platform/hooks/use_selected_region_display";
import {selectLatestEntry} from "../../web/src/features/ssr/redux/latest_entry_slice";
import {formatDate, humanDateFormatLong} from "../dates";
import {useAppSelector} from "./store_hooks";
import {useAccessDate} from "./useAccessDate";

export const usePageTitle = withDateGuard((): string => {
    const {path} = useRouteMatch();
    const {currentDate} = useAppSelector(selectLatestEntry);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore withDateGuard makes sure currentDate is not null
    const {upperBound: date} = useAccessDate(currentDate);
    const {i18n} = useTranslation();
    const currentMonthLabel = formatDate(date ?? "", humanDateFormatLong, i18n);
    const {citySlug, isKnownCity, regionLabel} = useSelectedRegionDisplay();

    const pageTitle = generatePageTitle(path, i18n, citySlug, isKnownCity, regionLabel);
    return `${pageTitle} — ${currentMonthLabel}`;
});

/** Picks the grammatically-declined `{city}` variant for known cities, or the plain `_region` fallback otherwise. */
const translateCityPageTitle = (
    i18n: i18n,
    key: string,
    isKnownCity: boolean,
    city: CitySlug,
    regionLabel: string
): string => (isKnownCity ? i18n.t(key, {city}) : i18n.t(`${key}_region`, {region: regionLabel}));

const generatePageTitle = (
    path: string,
    i18n: i18n,
    city: CitySlug,
    isKnownCity: boolean,
    regionLabel: string
): string | never => {
    switch (path) {
        case chartsRoutes.pl.properties.root:
        case chartsRoutes.en.properties.root:
            return i18n.t("home.page_title.properties");
        case chartsRoutes.pl.flats.root:
        case chartsRoutes.en.flats.root:
            return i18n.t("home.page_title.flats");
        case chartsRoutes.pl.houses.root:
        case chartsRoutes.en.houses.root:
            return i18n.t("home.page_title.houses");
        case chartsRoutes.pl.properties.city.root:
        case chartsRoutes.en.properties.city.root:
            return translateCityPageTitle(i18n, "dashboard.page_title.properties", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.flats.city.root:
        case chartsRoutes.en.flats.city.root:
            return translateCityPageTitle(i18n, "dashboard.page_title.flats", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.houses.city.root:
        case chartsRoutes.en.houses.city.root:
            return translateCityPageTitle(i18n, "dashboard.page_title.houses", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.properties.city.locationSummary:
        case chartsRoutes.en.properties.city.locationSummary:
            return translateCityPageTitle(
                i18n,
                "location_summary.page_title.properties",
                isKnownCity,
                city,
                regionLabel
            );
        case chartsRoutes.pl.flats.city.locationSummary:
        case chartsRoutes.en.flats.city.locationSummary:
            return translateCityPageTitle(i18n, "location_summary.page_title.flats", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.houses.city.locationSummary:
        case chartsRoutes.en.houses.city.locationSummary:
            return translateCityPageTitle(i18n, "location_summary.page_title.houses", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.properties.city.prices:
        case chartsRoutes.en.properties.city.prices:
            return translateCityPageTitle(i18n, "prices.page_title.properties", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.flats.city.prices:
        case chartsRoutes.en.flats.city.prices:
            return translateCityPageTitle(i18n, "prices.page_title.flats", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.properties.city.offer:
        case chartsRoutes.en.properties.city.offer:
            return translateCityPageTitle(i18n, "offer.page_title.properties", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.flats.city.offer:
        case chartsRoutes.en.flats.city.offer:
            return translateCityPageTitle(i18n, "offer.page_title.flats", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.properties.city.demand:
        case chartsRoutes.en.properties.city.demand:
            return translateCityPageTitle(i18n, "demand.page_title.properties", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.flats.city.demand:
        case chartsRoutes.en.flats.city.demand:
            return translateCityPageTitle(i18n, "demand.page_title.flats", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.properties.city.sold:
        case chartsRoutes.en.properties.city.sold:
            return translateCityPageTitle(i18n, "sold.page_title.properties", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.flats.city.sold:
        case chartsRoutes.en.flats.city.sold:
            return translateCityPageTitle(i18n, "sold.page_title.flats", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.properties.city.added:
        case chartsRoutes.en.properties.city.added:
            return translateCityPageTitle(i18n, "added.page_title.properties", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.flats.city.added:
        case chartsRoutes.en.flats.city.added:
            return translateCityPageTitle(i18n, "added.page_title.flats", isKnownCity, city, regionLabel);
        case chartsRoutes.pl.flats.fltr:
        case chartsRoutes.en.flats.fltr:
            return i18n.t("nav.fltr_dashboards");
        default:
            throw new Error(`Invalid path provided to generatePageTitle(): ${path}`);
    }
};
